import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { MainLayout, SocialHeadData, ViewProjectUpdateModalController } from '@Components';
import { FundDetailsPageController } from '@Views';
import { fetchServerSideData, getFundPath } from '@Helpers';
import { ApiErrorObject, Fund, ProjectUpdate } from '@Types';
import { API } from '@Services';
import { QueryParams } from '@App/paths';
import { UUID_REGEX } from '@Helpers/constants';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  let props = {};
  if (query && Number(query?.fundId)) {
    const fundId = Number(query.fundId);

    const accessCode = query.access_code as string | undefined;
    // Don't bother including access code in the request if it's not a valid UUID.
    const config = accessCode?.match(UUID_REGEX)
      ? { headers: { 'X-Fund-Access-Code': accessCode } }
      : undefined;
    const fundData = await fetchServerSideData<Fund & ApiErrorObject>(
      API.paths.fund(fundId),
      config,
    );

    if (fundData.code === 'NOT_FOUND') {
      return { notFound: true };
    }

    props = { ...props, fundData };
  }

  if (query[QueryParams.PROJECT_UPDATE]) {
    const projectUpdateId = Number(query[QueryParams.PROJECT_UPDATE]);
    const projectUpdateData = await fetchServerSideData<ProjectUpdate & ApiErrorObject>(
      API.paths.project_update(projectUpdateId),
    );

    if (projectUpdateData.code === 'NOT_FOUND') {
      return { notFound: true };
    }

    props = { ...props, projectUpdateData };
  }

  return { props };
};

interface FundDetailsPageProps {
  fundData?: Fund;
  projectUpdateData?: ProjectUpdate;
}

const FundDetailsPage = ({ fundData, projectUpdateData }: FundDetailsPageProps) => {
  return (
    <MainLayout>
      {fundData && (
        <>
          <Head>
            <title>{`${fundData.details.name} | ActionFunder`}</title>
          </Head>
          <SocialHeadData
            title={fundData.details.name}
            image={fundData.funding_profile.avatar_image}
            url={`https://www.actionfunder.org${getFundPath(fundData.id)}`}
            description={fundData.details.purpose}
          />
        </>
      )}
      {fundData && <FundDetailsPageController fundData={fundData} />}
      {projectUpdateData && (
        <ViewProjectUpdateModalController initialProjectUpdateData={projectUpdateData} />
      )}
    </MainLayout>
  );
};

export default FundDetailsPage;
